import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { multiStep } from '../MultiStepWizard/multiStep'
import { Form } from 'components/Wizards/Form/Form'
import Styles from './DesignMarketplaceWizard.module.scss'
import { SelectedImage } from 'types'
import { useCreateMediaVariantSync } from 'hooks/queries/mediaFiles/useCreateMediaVariant'
import { useEffect, useRef, useState } from 'react'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { Text } from 'blaze-ui/Text/Text'
import { Formik } from 'formik'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { getTemplatePreviewCloudinaryUrl } from 'components/Design/utils/getTemplatePreviewCloudinaryUrl'
import { MessageType } from 'antd/lib/message'
import { message } from 'antd'
import cx from 'classnames'
import { MultiStepSchema } from '../schemas/Types'
import { useWizardSchema } from '../Wizard/WizardContext'
import * as CSS from 'csstype'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props extends StepProps {
  isLoading?: boolean
  selectedImage: SelectedImage
  canRemoveBackground?: boolean
  onChangeCanRemoveBackground: (value: boolean) => void
  hasRemovedBackground?: boolean
  onChangeRemovedBackground: (value: boolean) => void
  cutoutUrl?: string
  setCutoutUrl: (value?: string) => void
}

export const RemoveBackground = multiStep<Props>(
  ({
    schema,
    selectedImage,
    canRemoveBackground,
    onChangeCanRemoveBackground,
    hasRemovedBackground,
    onChangeRemovedBackground,
    onSubmit,
    onBack,
    cutoutUrl,
    setCutoutUrl,
  }) => {
    const [isBackgroundRemoved, setIsBackgroundRemoved] = useState(false)
    const { isMobile } = useResponsiveDesign()
    const { mutate, isLoading } = useCreateMediaVariantSync()
    const wizardSchema = useWizardSchema() as MultiStepSchema
    const originalImageRef = useRef<HTMLImageElement>(null)

    const originalHeight = originalImageRef.current?.getBoundingClientRect().height
    const imageStyle: CSS.Properties = {
      height: isMobile ? `${originalHeight}px` || 0 : undefined,
    }

    let removingBackgroundToast: MessageType
    const originalUrl = getTemplatePreviewCloudinaryUrl(selectedImage.source_url || selectedImage.url, { height: 350 })
    const src =
      isBackgroundRemoved && cutoutUrl ? getTemplatePreviewCloudinaryUrl(cutoutUrl, { height: 350 }) : originalUrl

    const handleBack = (destroyToasts = true) => {
      if (destroyToasts) message.destroy()
      onChangeRemovedBackground(false)
      onChangeCanRemoveBackground(false)
      setCutoutUrl(undefined)
      onBack?.()
    }

    const removeBackground = () => {
      if (!hasRemovedBackground && !cutoutUrl && selectedImage.id && canRemoveBackground) {
        if (!removingBackgroundToast)
          removingBackgroundToast = showToastMessage('Removing Background..', 'generating', undefined, 0)

        mutate(
          {
            mediaFileId: selectedImage.id,
            timeout: 20000, // 20 seconds
            transformations: {
              remove_background: { crop: true },
            },
          },
          {
            onError: () => {
              message.destroy()
              setIsBackgroundRemoved(false)
              showToastMessage("We weren't able to remove the background of this image.", 'error')
              handleBack(false)
            },
            onSuccess: (data) => {
              message.destroy()
              onChangeRemovedBackground(true)
              setCutoutUrl(data.source_url)
            },
          },
        )
      }
      setIsBackgroundRemoved((value) => !value)
    }

    useEffect(() => {
      if (canRemoveBackground) removeBackground()
    }, [canRemoveBackground]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Formik
        validationSchema={schema}
        onSubmit={onSubmit}
        initialValues={schema.cast({ ...schema.getDefault() }, { assert: false })}
      >
        {({ setFieldValue }) => (
          <Form.Root>
            <Form.Section>
              <div className={cx(Styles.container, Styles.backgroundRemoval)}>
                {isLoading ? (
                  <LoadingShimmerer>
                    <img
                      alt="selected"
                      src={src}
                      ref={originalImageRef}
                      className={cx(Styles.selectedImage, Styles.isLoading)}
                    />
                  </LoadingShimmerer>
                ) : (
                  <>
                    <img alt="selected" src={src} className={Styles.selectedImage} style={imageStyle} />{' '}
                    <div className={cx(Styles.removeBackground, { [Styles.invisible]: !cutoutUrl })}>
                      <Text variant="largeList">Remove Background</Text>
                      <ToggleButton
                        name="Remove Background"
                        checked={isBackgroundRemoved}
                        handleChange={(e) => {
                          setFieldValue('initial_media_file_remove_background', !isBackgroundRemoved)
                          removeBackground()
                        }}
                        disabled={isLoading}
                      />
                    </div>
                  </>
                )}
              </div>
            </Form.Section>
            <Form.Actions isSticky>
              <Form.PreviousButton onPress={() => handleBack()} isDisabled={isLoading}>
                Back
              </Form.PreviousButton>
              <Form.PrimaryActions>
                <Form.SubmitButton isDisabled={isLoading} variant="primary">
                  {wizardSchema.steps.length === 3 ? schema.describe().meta.submitLabel : 'Generate Post'}
                </Form.SubmitButton>
              </Form.PrimaryActions>
            </Form.Actions>
          </Form.Root>
        )}
      </Formik>
    )
  },
)
